import React, { useMemo } from 'react';
import Select from './Select';
import { getMalaysiaNow } from '../../shared/utils/formatters';

export interface MonthPickerValue {
  year: number;
  /** 1-12, or null when "Full Year" is selected and `allowAllMonths` is set. */
  month: number | null;
}

interface MonthPickerProps {
  value: MonthPickerValue;
  onChange: (value: MonthPickerValue) => void;
  /** How many years back the year list reaches, including the current one. */
  yearsBack?: number;
  /** Adds a "Full Year" option that reports `month: null`. */
  allowAllMonths?: boolean;
  disabled?: boolean;
  className?: string;
  /** Rendered above the controls; pass null to hide them. */
  yearLabel?: string | null;
  monthLabel?: string | null;
}

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

/**
 * MonthPicker
 *
 * Paired year and month selects for month-scoped reports and charts.
 *
 * The year list is anchored to `getMalaysiaNow()` rather than the browser clock, so
 * a viewer in another timezone still sees the business's current year first.
 */
const MonthPicker: React.FC<MonthPickerProps> = ({
  value,
  onChange,
  yearsBack = 5,
  allowAllMonths = false,
  disabled = false,
  className = '',
  yearLabel = 'Year',
  monthLabel = 'Month'
}) => {
  const availableYears = useMemo(() => {
    const { year: currentYear } = getMalaysiaNow();
    return Array.from({ length: yearsBack }, (_, i) => currentYear - i);
  }, [yearsBack]);

  return (
    <div className={`grid grid-cols-2 gap-4 ${className}`}>
      <div>
        {yearLabel && (
          <label htmlFor="month-picker-year" className="block text-sm font-medium text-gray-700 mb-1">
            {yearLabel}
          </label>
        )}
        <Select
          id="month-picker-year"
          value={value.year}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, year: Number(e.target.value) })}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Select>
      </div>

      <div>
        {monthLabel && (
          <label htmlFor="month-picker-month" className="block text-sm font-medium text-gray-700 mb-1">
            {monthLabel}
          </label>
        )}
        <Select
          id="month-picker-month"
          value={value.month ?? ''}
          disabled={disabled}
          onChange={(e) =>
            onChange({ ...value, month: e.target.value === '' ? null : Number(e.target.value) })
          }
        >
          {allowAllMonths && <option value="">Full Year</option>}
          {MONTHS.map((month) => (
            <option key={month.value} value={month.value}>{month.label}</option>
          ))}
        </Select>
      </div>
    </div>
  );
};

export default MonthPicker;
